<template>
  <section
    class="two-cols-text-module-component module-container"
    :class="data.background_color_variation ? 'green-variation' : ''">
    <div class="inner-container">
      <p
        v-if="!data.background_color_variation"
        class="header-2 left-col"
        v-html="data.left_col" />

      <div
        v-else
        class="left-col-title">
        <div class="image-container animated-icon flex-1">
          <div
            :id="animTriggerClass"
            ref="lottieContainer"
            class="lottie-container"></div>
        </div>
      </div>

      <div
        v-if="!data.background_color_variation"
        class="right-col">
        <h2
          class="header-2 right-col-title"
          v-html="data.right_col.title" />
        <div
          class="right-col-text"
          v-html="data.right_col.text" />
      </div>

      <div
        v-else
        class="right-col">
        <div
          class="right-col-text"
          v-html="data.variation_right_col" />
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
   {
    "NAME": "TwoColsTextComponent",
    "CLASSNAME": "two-cols-text-module-component",
    "URL": "http://vuevitewp.local/test"
   }
  #ENDMETA */
defineProps(['data'])
import LottieWeb from 'lottie-web'
import logoJson from '@/assets/lottie/REVMED_LOGO.json'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import useUtils from '@/components/composables/useUtils'

gsap.registerPlugin(ScrollTrigger)

const { genUUID } = useUtils()
const animID = ref(genUUID())
const lottieAnimID = ref(genUUID())
const lottieContainer = ref()
const animTriggerClass = `anim-trigger-${animID.value}`

const animateOnScroll = () => {
  const anim = LottieWeb.loadAnimation({
    container: lottieContainer.value,
    renderer: 'svg',
    autoplay: false,
    loop: false,
    animationData: logoJson,
  })

  ScrollTrigger.create({
    trigger: `#${animTriggerClass}`,
    start: 'top-=200 center',
    end: 'bottom+=100 center',
    onUpdate: ({ progress }) => {
      anim.goToAndStop((progress + 0.3) * (anim.totalFrames - 1), true)
    },
    markers: false,
    id: lottieAnimID.value,
  })
}

onMounted(() => {
  animateOnScroll()
})

onBeforeUnmount(() => {
  gsap.set('.site-footer', {
    background: 'transparent',
  })
  const lottieAnim = ScrollTrigger.getById(lottieAnimID.value)
  if (lottieAnim) lottieAnim.kill()
})
</script>

<style lang="scss" scoped>
.two-cols-text-module-component {
  @apply py-5;

  .inner-container {
    @apply flex flex-col gap-20
    lg:flex-row lg:items-center lg:gap-[100px]
    xl:gap-[139px];

    .left-col {
      @apply text-balanced
      lg:w-[410px] 
      xl:w-[547px] xl:text-[60px]  xl:leading-[60px]  xl:tracking-[-1.2px];
    }
    .right-col {
      @apply flex flex-col gap-8
      lg:flex-1;
      &-title {
        @apply text-balanced;
      }
      &-text {
        @apply flex flex-col gap-8;
        &:deep(p) {
          &:first-child {
            @apply w-[70%] lg:w-full;
          }
          &:nth-child(2) {
            @apply w-[90%] lg:w-full;
          }
          &:nth-child(3) {
            @apply w-[90%] lg:w-full;
          }
          @apply font-FKGrotesk;
          a {
            @apply leading-10 underline font-bold lg:text-[22px];
          }
        }
      }
    }
  }

  &.green-variation {
    @apply bg-balanced;

    .inner-container {
      @apply gap-8 lg:items-start lg:gap-[88px];

      .left-col-title {
        @apply lg:flex-grow-0 lg:w-[406px];

        .lottie-container {
          @apply w-[261px] mx-auto lg:-translate-y-10 lg:w-[355px];
        }
      }

      .right-col {
        @apply gap-0 lg:flex-1;

        &:deep() {
          h1,
          h2,
          h3,
          h4,
          h5,
          h6 {
            @apply text-white header-4;
          }
          p {
            @apply text-white gap-5;
          }
        }
      }
    }
  }
}
</style>
